import type { FormEvent, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

type FormData = {
  company: string;
  name: string;
  email: string;
  phone: string;
  inquiryType: string;
  message: string;
};

type SubmitStatus = 'idle' | 'confirm' | 'submitting' | 'success' | 'error';

const initialFormData: FormData = {
  company: '',
  name: '',
  email: '',
  phone: '',
  inquiryType: '',
  message: ''
};

function Modal({
  isOpen,
  title,
  children
}: {
  isOpen: boolean;
  title: string;
  children: ReactNode;
}) {
  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-6 backdrop-blur-sm">
      <div className="w-full max-w-2xl rounded-2xl border border-white/10 bg-[#1a1d23] p-8 shadow-2xl">
        <h3 className="mb-6 text-2xl font-bold text-white" style={{ fontFamily: 'Inter, sans-serif' }}>
          {title}
        </h3>
        {children}
      </div>
    </div>
  );
}

export default function ContactSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('idle');
  const [charCount, setCharCount] = useState(0);
  const [submitError, setSubmitError] = useState('');
  const [formData, setFormData] = useState<FormData>(initialFormData);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const handleOpenConfirm = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const form = event.currentTarget;
    if (!form.reportValidity()) {
      return;
    }

    if (!agreed) {
      setSubmitError('プライバシーポリシーに同意の上、送信してください。');
      setSubmitStatus('error');
      return;
    }

    if (formData.message.length > 500) {
      setSubmitError('お問い合わせ内容は500文字以内で入力してください。');
      setSubmitStatus('error');
      return;
    }

    setSubmitError('');
    setSubmitStatus('confirm');
  };

  const handleConfirmSubmit = async () => {
    setSubmitStatus('submitting');
    setSubmitError('');

    try {
      const data = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        data.append(key, value);
      });
      data.append('privacy', agreed ? '同意する' : '');

      const res = await fetch('/send.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });

      if (!res.ok) {
        throw new Error('send_failed');
      }

      setSubmitStatus('success');
      setFormData(initialFormData);
      setAgreed(false);
      setCharCount(0);
    } catch (error) {
      console.error('送信エラー:', error);
      setSubmitError('送信に失敗しました。時間をおいて再度お試しください。');
      setSubmitStatus('error');
    }
  };

  return (
    <section id="contact" className="bg-[#0f1115] py-24 md:py-32" ref={ref}>
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div
          className={`flex flex-col lg:flex-row gap-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="lg:w-2/5">
            <span className="text-xs font-semibold tracking-widest text-[#38bdf8] uppercase mb-3 block">Contact</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>
              お問い合わせ
            </h2>
            <p className="text-gray-300 text-[15px] leading-relaxed mb-8">
              サービスに関するご質問・ご相談はお気軽にどうぞ。
              内容確認後、担当者より順次ご返信いたします。
            </p>

            <div className="space-y-4">
              {[
                { icon: 'ri-map-pin-line', text: '東京都板橋区小豆沢3-3-4-305' },
                { icon: 'ri-time-line', text: '平日 9:00〜18:00' },
              ].map((item) => (
                <div key={item.text} className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#38bdf8]/10">
                    <i className={`${item.icon} text-[#38bdf8] text-sm`} />
                  </div>
                  <span className="text-gray-300 text-[15px]">{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:w-3/5">
            <form
              id="contact-form"
              onSubmit={handleOpenConfirm}
              className="bg-[#1a1d23] border border-white/5 rounded-2xl p-8 space-y-5"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-gray-300 text-sm mb-1.5">会社名</label>
                  <input
                    type="text"
                    name="company"
                    placeholder="株式会社〇〇"
                    value={formData.company}
                    onChange={(event) => setFormData({ ...formData, company: event.target.value })}
                    className="w-full bg-[#0f1115] border border-white/10 rounded-md px-4 py-3 text-white text-[15px] placeholder-gray-500 focus:outline-none focus:border-[#38bdf8]/60 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 text-sm mb-1.5">お名前 <span className="text-red-400">*</span></label>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="山田 太郎"
                    value={formData.name}
                    onChange={(event) => setFormData({ ...formData, name: event.target.value })}
                    className="w-full bg-[#0f1115] border border-white/10 rounded-md px-4 py-3 text-white text-[15px] placeholder-gray-500 focus:outline-none focus:border-[#38bdf8]/60 transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-300 text-sm mb-1.5">メールアドレス <span className="text-red-400">*</span></label>
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="example@company.co.jp"
                  value={formData.email}
                  onChange={(event) => setFormData({ ...formData, email: event.target.value })}
                  className="w-full bg-[#0f1115] border border-white/10 rounded-md px-4 py-3 text-white text-[15px] placeholder-gray-500 focus:outline-none focus:border-[#38bdf8]/60 transition-colors"
                />
              </div>

              <div>
                <label className="block text-gray-300 text-sm mb-1.5">電話番号</label>
                <input
                  type="tel"
                  name="phone"
                  placeholder="03-0000-0000"
                  value={formData.phone}
                  onChange={(event) => setFormData({ ...formData, phone: event.target.value })}
                  className="w-full bg-[#0f1115] border border-white/10 rounded-md px-4 py-3 text-white text-[15px] placeholder-gray-500 focus:outline-none focus:border-[#38bdf8]/60 transition-colors"
                />
              </div>

              <div>
                <label className="block text-gray-300 text-sm mb-1.5">
                  問い合わせ種別 <span className="text-red-400">*</span>
                </label>
                <select
                  name="inquiryType"
                  required
                  value={formData.inquiryType}
                  onChange={(event) => setFormData({ ...formData, inquiryType: event.target.value })}
                  className="w-full bg-[#0f1115] border border-white/10 rounded-md px-4 py-3 text-white text-[15px] focus:outline-none focus:border-[#38bdf8]/60 transition-colors"
                >
                  <option value="" className="text-gray-500">選択してください</option>
                  <option value="クラウド型経理システムについて">クラウド型経理システムについて</option>
                  <option value="関連事業について">関連事業について</option>
                  <option value="その他のご相談">その他のご相談</option>
                </select>
              </div>

              <div>
                <label className="block text-gray-300 text-sm mb-1.5">
                  お問い合わせ内容 <span className="text-red-400">*</span>
                  <span className="ml-2 text-gray-600">{charCount}/500</span>
                </label>
                <textarea
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  placeholder="ご質問・ご相談内容をご記入ください"
                  value={formData.message}
                  onChange={(event) => {
                    setFormData({ ...formData, message: event.target.value });
                    setCharCount(event.target.value.length);
                  }}
                  className="w-full bg-[#0f1115] border border-white/10 rounded-md px-4 py-3 text-white text-[15px] placeholder-gray-500 focus:outline-none focus:border-[#38bdf8]/60 transition-colors resize-none"
                />
              </div>

              <div className="flex items-start gap-3">
                <button
                  type="button"
                  onClick={() => setAgreed(!agreed)}
                  className={`mt-0.5 w-5 h-5 flex items-center justify-center rounded border-2 transition-colors shrink-0 cursor-pointer ${
                    agreed ? 'bg-[#38bdf8] border-[#38bdf8]' : 'border-gray-600 bg-transparent'
                  }`}
                  aria-checked={agreed}
                  role="checkbox"
                >
                  {agreed && <i className="ri-check-line text-white text-xs" />}
                </button>
                <p className="text-gray-300 text-sm leading-relaxed">
                  <Link
                    to="/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#38bdf8] hover:underline"
                  >
                    プライバシーポリシー
                  </Link>
                  に同意の上、送信してください。
                </p>
              </div>

              <button
                type="submit"
                className="w-full py-3 rounded-md bg-gradient-to-r from-[#0ea5e9] to-[#38bdf8] text-white font-semibold text-sm hover:opacity-90 transition-opacity whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
              >
                入力内容を確認する
                <i className="ri-arrow-right-line" />
              </button>
            </form>
          </div>
        </div>
      </div>

      <Modal isOpen={submitStatus === 'confirm'} title="入力内容の確認">
        <div className="space-y-4 text-[15px] text-gray-200">
          <div className="rounded-2xl border border-white/10 bg-[#0f1115] p-5">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <div className="mb-1 text-gray-400">会社名</div>
                <div>{formData.company || '未入力'}</div>
              </div>
              <div>
                <div className="mb-1 text-gray-400">お名前</div>
                <div>{formData.name}</div>
              </div>
              <div>
                <div className="mb-1 text-gray-400">メールアドレス</div>
                <div>{formData.email}</div>
              </div>
              <div>
                <div className="mb-1 text-gray-400">電話番号</div>
                <div>{formData.phone || '未入力'}</div>
              </div>
              <div>
                <div className="mb-1 text-gray-400">問い合わせ種別</div>
                <div>{formData.inquiryType || '未選択'}</div>
              </div>
              <div className="md:col-span-2">
                <div className="mb-1 text-gray-400">お問い合わせ内容</div>
                <div className="whitespace-pre-wrap leading-relaxed">{formData.message}</div>
              </div>
              <div className="md:col-span-2">
                <div className="mb-1 text-gray-400">プライバシーポリシー同意</div>
                <div>{agreed ? '同意する' : '未同意'}</div>
              </div>
            </div>
          </div>
          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={() => setSubmitStatus('idle')}
              className="rounded-xl border border-white/10 px-5 py-3 text-gray-200 transition hover:bg-white/5"
            >
              修正する
            </button>
            <button
              type="button"
              onClick={handleConfirmSubmit}
              className="rounded-xl bg-gradient-to-r from-[#0ea5e9] to-[#38bdf8] px-5 py-3 font-semibold text-white transition hover:opacity-90"
            >
              この内容で送信する
            </button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={submitStatus === 'submitting'} title="送信中">
        <div className="space-y-4 text-[15px] text-gray-200">
          <p>お問い合わせを送信しています。しばらくお待ちください。</p>
          <div className="h-2 overflow-hidden rounded-full bg-white/10">
            <div className="h-full w-1/2 animate-pulse rounded-full bg-gradient-to-r from-[#0ea5e9] to-[#38bdf8]"></div>
          </div>
        </div>
      </Modal>

      <Modal isOpen={submitStatus === 'success'} title="送信完了">
        <div className="space-y-6 text-[15px] text-gray-200">
          <p>お問い合わせを受け付けました。担当者より順次ご連絡いたします。</p>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setSubmitStatus('idle')}
              className="rounded-xl bg-gradient-to-r from-[#0ea5e9] to-[#38bdf8] px-5 py-3 font-semibold text-white transition hover:opacity-90"
            >
              閉じる
            </button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={submitStatus === 'error'} title="送信エラー">
        <div className="space-y-6 text-[15px] text-gray-200">
          <p>{submitError}</p>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setSubmitStatus('idle')}
              className="rounded-xl border border-white/10 px-5 py-3 text-gray-200 transition hover:bg-white/5"
            >
              閉じる
            </button>
          </div>
        </div>
      </Modal>
    </section>
  );
}
